import React, { useCallback, useEffect, useState } from "react";
import { Download, Crown, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

const PLAN = { trial: "Gratis", pro: "Pro", premium: "Premium", vip: "VIP" };
export const refreshUsage = () => window.dispatchEvent(new Event("vitled:usage"));

// Floating export-allowance pill, always visible when signed in (bottom-left, collapsible).
export const UsageBar = () => {
  const { user } = useAuth();
  const { L } = useLang();
  const [u, setU] = useState(null);
  const [open, setOpen] = useState(() => window.location.pathname !== "/studio");
  const load = useCallback(() => { if (user) api.get("/me/usage").then((r) => setU(r.data)).catch(() => {}); }, [user]);
  useEffect(() => { load(); window.addEventListener("vitled:usage", load); return () => window.removeEventListener("vitled:usage", load); }, [load]);
  if (!user || !u) return null;
  const pct = Math.min(100, (u.exports_used / u.exports_limit) * 100);
  const out = u.exports_remaining <= 0;
  const color = out ? "#ef4444" : pct >= 66 ? "#f59e0b" : "#FACC15";
  return (
    <div className="fixed bottom-4 right-4 z-40" data-testid="usage-bar">
      {open ? (
        <div className="w-64 rounded-2xl border border-white/10 bg-zinc-950/90 backdrop-blur-xl p-3.5 shadow-[0_20px_60px_-20px_rgba(0,0,0,.9)]">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white"><Download className="h-3.5 w-3.5" style={{ color }} />{L("Exportaciones", "Exports")}</span>
            <span className="flex items-center gap-1.5">
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold text-zinc-200" data-testid="usage-plan">{PLAN[u.plan] || u.plan}</span>
              <button onClick={() => setOpen(false)} data-testid="usage-collapse" className="text-zinc-500 hover:text-white"><ChevronDown className="h-4 w-4" /></button>
            </span>
          </div>
          <div className="mt-2.5 flex items-end gap-1"><span className="text-2xl font-black text-white tabular-nums" data-testid="usage-count">{u.exports_used}</span><span className="text-sm text-zinc-500 mb-0.5">/ {u.exports_limit}</span><span className="ml-auto text-[10px] text-zinc-500 mb-1">{u.period === "month" ? L("este mes", "this month") : L("en total", "total")}</span></div>
          <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color, boxShadow: `0 0 12px ${color}` }} /></div>
          {(out || u.plan === "trial") && (
            <a href="/#pricing" onClick={() => setOpen(false)} data-testid="usage-upgrade" className="mt-3 flex items-center justify-center gap-1.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 py-2 text-xs font-extrabold">
              <Crown className="h-3.5 w-3.5" />{out ? L("Sin exportaciones · Mejorar plan", "No exports left · Upgrade") : L("Más exportaciones con Pro", "More exports with Pro")}
            </a>
          )}
        </div>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="usage-expand" className="flex items-center gap-2 rounded-full border border-white/10 bg-zinc-950/90 backdrop-blur-xl pl-2 pr-3 py-1.5 shadow-lg">
          <span className="h-6 w-6 rounded-full grid place-items-center text-[10px] font-black" style={{ background: color }}>{u.exports_remaining}</span>
          <span className="text-xs font-semibold text-white">{u.exports_used}/{u.exports_limit}</span>
        </button>
      )}
    </div>
  );
};
